import type { TFunction } from 'i18next';

import type {
  ReceiveLossTableData,
  ReceiveTemperatureRangeDto,
  ReceiveDangerousGoodDto,
  ReceiveSubprojectDto
} from '@/entities/receive';
import { formatDisplayDate } from '@/shared/lib';
import type { ColumnPreset, DataTableColumn } from '@/shared/ui/data-table';
import { TemperatureRangeBadge } from '@/shared/ui/temperature-range-badge';

const EMPTY_CELL = '-';

/** `lostType` enum → existing i18n label. */
const LOSS_TYPE_LABELS: Record<string, { key: string; fallback: string }> = {
  DID_NOT_ARRIVE: { key: 'dispatch.never.arrive.label', fallback: 'Will Never arrive' },
  LOST_IN_TRANSIT: { key: 'receive.lostintransit.label', fallback: 'Lost in Transit' }
};

const renderLossType = (code: string, t: TFunction) => {
  const label = LOSS_TYPE_LABELS[code];
  return label ? t(label.key, label.fallback) : code || EMPTY_CELL;
};

/** Scalar quantity as `qty unit` (backend sends per-line scalars, not arrays). */
const renderQuantity = (qty: number | null | undefined, unit: string | undefined) =>
  qty == null ? EMPTY_CELL : `${qty}${unit ? ` ${unit}` : ''}`;

const renderTemperatureRange = (range: ReceiveTemperatureRangeDto | null | undefined) =>
  range ? <TemperatureRangeBadge min={range.minTemp} max={range.maxTemp} /> : EMPTY_CELL;

const renderDangerousGood = (good: ReceiveDangerousGoodDto | null | undefined) =>
  good ? (
    <span title={good.classDescription}>
      {good.unId}
      {good.description ? ` — ${good.description}` : ''}
    </span>
  ) : (
    EMPTY_CELL
  );

const renderSubprojects = (subprojects: ReceiveSubprojectDto[] | undefined) =>
  subprojects?.length
    ? subprojects.map((subproject) => subproject.code || subproject.description || EMPTY_CELL).join(', ')
    : EMPTY_CELL;

/**
 * Lost or Not Sent Items columns. The default-visible set + order match the
 * user's screenshots; the remaining detail columns are hidden by default and
 * surfaced via Customize Columns / the Medical View preset. Column order below
 * is the Customize-Columns dialog order (all 25).
 */
export const getReceiveLossesColumns = (t: TFunction): DataTableColumn<ReceiveLossTableData>[] => [
  {
    id: 'lossDate',
    accessorKey: 'lossDate',
    header: t('dispatch.loss.date.label', 'Date of loss'),
    description: t('receive.losses.columns.lossDate'),
    width: 'w-[110px]',
    cell: (row) => <div className="text-xs text-muted-foreground">{formatDisplayDate(row.lossDate)}</div>,
    enableSorting: true,
    sortFieldId: 'lossDate'
  },
  {
    id: 'lostAt',
    accessorKey: 'lostAt',
    header: t('dispatch.loss.location.label', 'Lost at Location'),
    description: t('receive.losses.columns.lostAt'),
    width: 'w-[250px]',
    cell: (row) => row.lostAt || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'lostAt'
  },
  {
    id: 'lossType',
    accessorKey: 'lossType',
    header: t('receive.losstype.label', 'Type of loss'),
    description: t('receive.losses.columns.lossType'),
    width: 'w-[140px]',
    cell: (row) => renderLossType(row.lossType, t),
    enableSorting: true,
    sortFieldId: 'lossType'
  },
  {
    id: 'transportCompany',
    accessorKey: 'transportCompany',
    header: t('journey.transportCompany.label', 'Transport Company'),
    description: t('receive.losses.columns.transportCompany'),
    width: 'w-[150px]',
    cell: (row) => row.transportCompany || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'transportCompany'
  },
  {
    id: 'consignmentNumber',
    accessorKey: 'consignmentNumber',
    header: t('request.cmrNumber.label', 'Consignment Number'),
    description: t('receive.losses.columns.consignmentNumber'),
    width: 'w-[130px]',
    cell: (row) => <span className="font-mono text-sm">{row.consignmentNumber}</span>,
    enableSorting: true,
    sortFieldId: 'consignmentNumber'
  },
  {
    id: 'productDescription',
    accessorKey: 'productDescription',
    header: t('product.description.label', 'Product Category'),
    description: t('receive.losses.columns.productDescription'),
    width: 'w-[220px]',
    cell: (row) => row.productDescription || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'product'
  },
  {
    id: 'ownerItemDescription',
    accessorKey: 'ownerItemDescription',
    header: t('tracking.servicereq.desc.label', 'Description'),
    description: t('receive.losses.columns.ownerItemDescription'),
    width: 'w-[240px]',
    cell: (row) => row.ownerItemDescription || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'ownerItemDesc'
  },
  {
    id: 'lostQty',
    accessorKey: 'lostQty',
    header: t('tracking.servicereq.unitqty.label', 'Inventory Unit Quantity'),
    description: t('receive.losses.columns.lostQty'),
    width: 'w-[140px]',
    cell: (row) => renderQuantity(row.lostQty, row.unit),
    enableSorting: true,
    sortFieldId: 'ltuQty'
  },
  {
    id: 'totalWeight',
    accessorKey: 'totalWeight',
    header: t('consignment.columns.weightKg', 'Weight (kg)'),
    description: t('receive.losses.columns.totalWeight'),
    width: 'w-[100px]',
    cell: (row) => (typeof row.totalWeight === 'number' ? row.totalWeight.toFixed(2) : EMPTY_CELL),
    enableSorting: false
  },
  {
    id: 'totalVolume',
    accessorKey: 'totalVolume',
    header: t('consignment.columns.volumeM3', 'Volume (m³)'),
    description: t('receive.losses.columns.totalVolume'),
    width: 'w-[100px]',
    cell: (row) => (typeof row.totalVolume === 'number' ? row.totalVolume.toFixed(2) : EMPTY_CELL),
    enableSorting: false
  },

  /* --- detail columns (hidden by default) ---------------------------------- */
  {
    id: 'reportingQty',
    header: t('tracking.servicereq.reportingqty.label', 'Reporting Unit Quantity'),
    description: t('receive.losses.columns.reportingQty'),
    width: 'w-[160px]',
    cell: (row) => renderQuantity(row.reportingQty, row.reportingUnit),
    enableSorting: false,
    visible: false
  },
  {
    id: 'bundleQty',
    header: t('tracking.servicereq.handlingqty.label', 'Handling Unit Quantity'),
    description: t('receive.losses.columns.bundleQty'),
    width: 'w-[160px]',
    cell: (row) => renderQuantity(row.bundleQty, row.bundleUnit),
    enableSorting: false,
    visible: false
  },
  {
    id: 'totalValue',
    accessorKey: 'totalValue',
    header: t('tracking.servicereq.totval.label', 'Total Value (USD)'),
    description: t('receive.losses.columns.totalValue'),
    width: 'w-[120px]',
    cell: (row) => (typeof row.totalValue === 'number' ? row.totalValue.toLocaleString() : EMPTY_CELL),
    enableSorting: false,
    visible: false
  },
  {
    id: 'expiryDate',
    accessorKey: 'expiryDate',
    header: t('requestline.expiryDate.label', 'Expiry Date'),
    description: t('receive.losses.columns.expiryDate'),
    width: 'w-[120px]',
    cell: (row) =>
      row.expiryDate ? (
        <div className="text-xs text-muted-foreground">{formatDisplayDate(row.expiryDate)}</div>
      ) : (
        EMPTY_CELL
      ),
    enableSorting: true,
    sortFieldId: 'expiryDate',
    visible: false
  },
  {
    id: 'temperatureRange',
    header: t('tracking.servicereq.temprange.label', 'Temperature Range'),
    description: t('receive.losses.columns.temperatureRange'),
    width: 'w-[140px]',
    cell: (row) => renderTemperatureRange(row.temperatureRange),
    enableSorting: false,
    visible: false
  },
  {
    id: 'batchNumber',
    accessorKey: 'batchNumber',
    header: t('requestline.batchNumber.label', 'Batch Number'),
    description: t('receive.losses.columns.batchNumber'),
    width: 'w-[130px]',
    cell: (row) => row.batchNumber || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'batchNumber',
    visible: false
  },
  {
    id: 'origin',
    accessorKey: 'origin',
    header: t('journey.origin.label', 'Origin Location'),
    description: t('receive.losses.columns.origin'),
    width: 'w-[230px]',
    cell: (row) => row.origin || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'origin',
    visible: false
  },
  {
    id: 'destination',
    accessorKey: 'destination',
    header: t('convoy.destination.label', 'Destination'),
    description: t('receive.losses.columns.destination'),
    width: 'w-[230px]',
    cell: (row) => row.destination || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'destination',
    visible: false
  },
  {
    id: 'waybill',
    accessorKey: 'waybill',
    header: t('cmrlist.waybill.label', 'Waybill'),
    description: t('receive.losses.columns.waybill'),
    width: 'w-[130px]',
    cell: (row) => row.waybill || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'waybill',
    visible: false
  },
  {
    id: 'plate',
    accessorKey: 'plate',
    header: t('journey.vehicleIdnf.label', 'Vehicle Id'),
    description: t('receive.losses.columns.plate'),
    width: 'w-[120px]',
    cell: (row) => row.plate || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'plate',
    visible: false
  },
  {
    id: 'senderOrganization',
    accessorKey: 'senderOrganization',
    header: t('newcmr.senderorg.label', 'Sender Organization'),
    description: t('receive.losses.columns.senderOrganization'),
    width: 'w-[200px]',
    cell: (row) => row.senderOrganization || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'senderOrg',
    visible: false
  },
  {
    id: 'receiverOrganization',
    accessorKey: 'receiverOrganization',
    header: t('newcmr.receiverorg.label', 'Receiver Organization'),
    description: t('receive.losses.columns.receiverOrganization'),
    width: 'w-[200px]',
    cell: (row) => row.receiverOrganization || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'receiverOrg',
    visible: false
  },
  {
    id: 'dangerousGood',
    header: t('requestline.dangerousGoodsUnid.label', 'Dangerous Goods UN Identification number'),
    description: t('receive.losses.columns.dangerousGood'),
    width: 'w-[180px]',
    cell: (row) => renderDangerousGood(row.dangerousGood),
    enableSorting: false,
    visible: false
  },
  {
    id: 'ownersReference',
    accessorKey: 'ownersReference',
    header: t('request.ownersRef.label', "Owner's Reference"),
    description: "Owner's reference",
    width: 'w-[130px]',
    cell: (row) => row.ownersReference || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'ownersRef',
    visible: false
  },
  {
    id: 'subprojects',
    header: t('subproject.idnf.label', 'Sub-project code'),
    description: t('receive.losses.columns.ownersReference'),
    width: 'w-[130px]',
    cell: (row) => renderSubprojects(row.subprojects),
    enableSorting: false,
    visible: false
  }
];

/** Column presets — Medical View mirrors the screenshot's Medical View marks. */
export const getReceiveLossesColumnPresets = (t: TFunction): ColumnPreset[] => [
  {
    id: 'medical-view',
    label: t('gen.customize.medview.button', 'Medical View'),
    columnIds: [
      'lossDate',
      'lostAt',
      'lossType',
      'consignmentNumber',
      'ownerItemDescription',
      'lostQty',
      'totalValue',
      'expiryDate',
      'temperatureRange',
      'batchNumber',
      'receiverOrganization'
    ]
  }
];
